/**
 * Singular SDK initialization (native only).
 *
 * Singular is integrated as app-local Capacitor plugins — ios/App/App/SingularPlugin.swift
 * (SPM) and android/.../SingularPlugin.java (com.singular.sdk). We talk to both through
 * Capacitor's registerPlugin bridge — no Cordova global, no `deviceready` dance.
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

const SDK_KEY = import.meta.env.VITE_SINGULAR_SDK_KEY || '';
const SDK_SECRET = import.meta.env.VITE_SINGULAR_SDK_SECRET || '';

export interface SingularPlugin {
  initialize(options: {
    apiKey: string;
    secret: string;
    customUserId?: string;
    loggingEnabled?: boolean;
  }): Promise<void>;
  event(options: { name: string }): Promise<void>;
  eventWithArgs(options: { name: string; args: Record<string, unknown> }): Promise<void>;
  revenue(options: { currency: string; amount: number; args?: Record<string, unknown> }): Promise<void>;
  setCustomUserId(options: { userId: string }): Promise<void>;
  unsetCustomUserId(): Promise<void>;
  stopAllTracking(): Promise<void>;
  resumeAllTracking(): Promise<void>;
}

const Singular = registerPlugin<SingularPlugin>('Singular');

/** Returns the plugin proxy, or null on web (where the native impl is absent). */
export function getSingular(): SingularPlugin | null {
  if (!Capacitor.isNativePlatform()) return null;
  return Singular;
}

let isConfigured = false;
let configurationPromise: Promise<boolean> | null = null;

/**
 * Initialize the Singular SDK. Safe to call multiple times — configures once.
 * Pass the logged-in user id (if known) to bind attribution from the first session.
 */
export async function initializeSingular(userId?: string): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  if (isConfigured) return true;
  if (configurationPromise) return configurationPromise;
  configurationPromise = doConfiguration(userId);
  return configurationPromise;
}

async function doConfiguration(userId?: string): Promise<boolean> {
  if (!SDK_KEY || !SDK_SECRET) {
    console.error('Singular: missing VITE_SINGULAR_SDK_KEY / VITE_SINGULAR_SDK_SECRET');
    configurationPromise = null;
    return false;
  }

  try {
    await Singular.initialize({
      apiKey: SDK_KEY,
      secret: SDK_SECRET,
      customUserId: userId,
      // Logging enabled for all builds for now while we verify attribution.
      loggingEnabled: true,
    });
    isConfigured = true;
    console.log('Singular: SDK initialized on', Capacitor.getPlatform());
    return true;
  } catch (err) {
    console.error('Singular: init failed', err);
    configurationPromise = null; // allow a later retry
    return false;
  }
}

export function isSingularConfigured(): boolean {
  return isConfigured;
}
